import { supabase, supabaseAdmin } from '../lib/supabaseClient';

export type DiscountType = 'percentage' | 'fixed';
export type OrderType = 'delivery' | 'takeaway' | 'dine_in';

interface CouponRow {
  id: string;
  code: string;
  title: string;
  description: string | null;
  discount_type: DiscountType;
  discount_value: number;
  minimum_order: number | null;
  valid_from: string | null;
  valid_until: string | null;
  usage_limit: number | null;
  used_count: number | null;
  usage_per_user: number | null;
  is_active: boolean;
  is_user_specific: boolean;
  applies_to_order_types: OrderType[] | null;
  created_at: string;
  updated_at: string;
}

export interface CouponRecord {
  id: string;
  code: string;
  title: string;
  description: string | null;
  discountType: DiscountType;
  discountValue: number;
  minimumOrder: number | null;
  validFrom: string | null;
  validUntil: string | null;
  usageLimit: number | null;
  usedCount: number | null;
  usagePerUser: number | null;
  isActive: boolean;
  isUserSpecific: boolean;
  orderTypes: OrderType[];
  createdAt: string;
  updatedAt: string;
}

const mapCoupon = (row: CouponRow): CouponRecord => ({
  id: row.id,
  code: row.code,
  title: row.title,
  description: row.description,
  discountType: row.discount_type,
  discountValue: row.discount_value,
  minimumOrder: row.minimum_order,
  validFrom: row.valid_from,
  validUntil: row.valid_until,
  usageLimit: row.usage_limit,
  usedCount: row.used_count,
  usagePerUser: row.usage_per_user,
  isActive: Boolean(row.is_active),
  isUserSpecific: Boolean(row.is_user_specific),
  orderTypes: row.applies_to_order_types ?? ['delivery', 'takeaway', 'dine_in'],
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export async function listCoupons(): Promise<CouponRecord[]> {
  const { data, error } = await supabase
    .from('coupons')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).map(mapCoupon);
}

export interface UpsertCouponInput {
  id?: string;
  code: string;
  title: string;
  description?: string | null;
  discountType: DiscountType;
  discountValue: number;
  minimumOrder?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  usageLimit?: number | null;
  usagePerUser?: number | null;
  isActive?: boolean;
  orderTypes?: OrderType[];
  isUserSpecific?: boolean;
}

export async function upsertCoupon(input: UpsertCouponInput): Promise<CouponRecord> {
  const payload = {
    code: input.code.toUpperCase(),
    title: input.title,
    description: input.description ?? null,
    discount_type: input.discountType,
    discount_value: input.discountValue,
    minimum_order: input.minimumOrder ?? null,
    valid_from: input.validFrom ?? null,
    valid_until: input.validUntil ?? null,
    usage_limit: input.usageLimit ?? null,
    usage_per_user: input.usagePerUser ?? null,
    is_active: input.isActive ?? true,
    is_user_specific: input.isUserSpecific ?? false,
    applies_to_order_types: input.orderTypes ?? ['delivery', 'takeaway', 'dine_in'],
  };

  const query = supabase
    .from('coupons')
    .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
      onConflict: 'code',
    })
    .select()
    .single();

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return mapCoupon(data as CouponRow);
}

export async function deleteCoupon(couponId: string) {
  const { error } = await supabase.from('coupons').delete().eq('id', couponId);
  if (error) {
    throw error;
  }
}

export async function setCouponActive(couponId: string, isActive: boolean) {
  const { error } = await supabase
    .from('coupons')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', couponId);
  if (error) {
    throw error;
  }
}

export async function assignCouponToUser(couponId: string, userId: string, quantity = 1) {
  const grantCount = Math.max(1, Math.floor(quantity));
  const nowIso = new Date().toISOString();
  const rows = Array.from({ length: grantCount }).map(() => ({
    user_id: userId,
    coupon_id: couponId,
    is_used: false,
    assigned_at: nowIso,
  }));

  const client = supabaseAdmin ?? supabase;
  const { error } = await client.from('user_coupons').insert(rows);
  if (error) {
    throw error;
  }
}
